import { useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils/cn';

type TagInputProps = {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  maxTagLength?: number;
};

/**
 * Comma- or Enter-separated tag chips. No fancy autocomplete in Phase 1
 * — the input just lets the qari type freely. Trims, dedupes, caps.
 */
export function TagInput({
  value,
  onChange,
  placeholder,
  disabled,
  maxTagLength = 40,
}: TagInputProps) {
  const [draft, setDraft] = useState('');

  function commit() {
    const next = draft.trim();
    if (!next) return;
    if (next.length > maxTagLength) return;
    if (value.includes(next)) {
      setDraft('');
      return;
    }
    onChange([...value, next]);
    setDraft('');
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  return (
    <div
      className={cn(
        'flex flex-wrap gap-1.5 px-2 py-1.5 rounded-lg border bg-transparent',
        'border-ink-200/80 dark:border-ink-800/80',
        'focus-within:ring-2 focus-within:ring-accent/40 focus-within:border-accent',
        disabled && 'opacity-60 pointer-events-none',
      )}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-ink-100 dark:bg-ink-800 text-xs text-ink-800 dark:text-ink-100"
        >
          {tag}
          <button
            type="button"
            aria-label={`Remove ${tag}`}
            onClick={() => remove(tag)}
            className="text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKey}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : ''}
        disabled={disabled}
        className={cn(
          'flex-1 min-w-[120px] bg-transparent text-sm py-1',
          'text-ink-900 dark:text-ink-50',
          'placeholder:text-ink-400 dark:placeholder:text-ink-600',
          'focus:outline-none',
        )}
      />
    </div>
  );
}
